// B2B Widget Content Settings Page
// Manage specializations (trip types, destinations) and featured offers
import { useState, useEffect } from 'react';
import { useB2B } from '../context/B2BContext';

// ═══════════════════════════════════════════════════════════════════
// PREDEFINED OPTIONS
// ═══════════════════════════════════════════════════════════════════

const TRIP_TYPES = [
  { value: 'beach', label: 'Beach', emoji: '🏖️' },
  { value: 'honeymoon', label: 'Honeymoon', emoji: '💑' },
  { value: 'family', label: 'Family', emoji: '👨‍👩‍👧‍👦' },
  { value: 'adventure', label: 'Adventure', emoji: '🏔️' },
  { value: 'cruise', label: 'Cruise', emoji: '🚢' },
  { value: 'ski', label: 'Ski', emoji: '⛷️' },
  { value: 'luxury', label: 'Luxury', emoji: '✨' },
  { value: 'budget', label: 'Budget', emoji: '💰' },
  { value: 'solo', label: 'Solo', emoji: '🧳' },
  { value: 'group', label: 'Group', emoji: '👥' },
  { value: 'wellness', label: 'Wellness', emoji: '🧘' },
  { value: 'golf', label: 'Golf', emoji: '⛳' }
];

// Destinations grouped by region for better organization
const DESTINATIONS_BY_REGION = {
  'Europe': [
    { value: 'austria', label: 'Austria', emoji: '🇦🇹' },
    { value: 'belgium', label: 'Belgium', emoji: '🇧🇪' },
    { value: 'croatia', label: 'Croatia', emoji: '🇭🇷' },
    { value: 'czech_republic', label: 'Czech Republic', emoji: '🇨🇿' },
    { value: 'denmark', label: 'Denmark', emoji: '🇩🇰' },
    { value: 'finland', label: 'Finland', emoji: '🇫🇮' },
    { value: 'france', label: 'France', emoji: '🇫🇷' },
    { value: 'germany', label: 'Germany', emoji: '🇩🇪' },
    { value: 'greece', label: 'Greece', emoji: '🇬🇷' },
    { value: 'hungary', label: 'Hungary', emoji: '🇭🇺' },
    { value: 'iceland', label: 'Iceland', emoji: '🇮🇸' },
    { value: 'ireland', label: 'Ireland', emoji: '🇮🇪' },
    { value: 'italy', label: 'Italy', emoji: '🇮🇹' },
    { value: 'netherlands', label: 'Netherlands', emoji: '🇳🇱' },
    { value: 'norway', label: 'Norway', emoji: '🇳🇴' },
    { value: 'poland', label: 'Poland', emoji: '🇵🇱' },
    { value: 'portugal', label: 'Portugal', emoji: '🇵🇹' },
    { value: 'romania', label: 'Romania', emoji: '🇷🇴' },
    { value: 'scotland', label: 'Scotland', emoji: '🏴󠁧󠁢󠁳󠁣󠁴󠁿' },
    { value: 'spain', label: 'Spain', emoji: '🇪🇸' },
    { value: 'sweden', label: 'Sweden', emoji: '🇸🇪' },
    { value: 'switzerland', label: 'Switzerland', emoji: '🇨🇭' },
    { value: 'turkey', label: 'Turkey', emoji: '🇹🇷' },
    { value: 'uk', label: 'UK', emoji: '🇬🇧' }
  ],
  'Asia & Pacific': [
    { value: 'australia', label: 'Australia', emoji: '🇦🇺' },
    { value: 'bali', label: 'Bali', emoji: '🇮🇩' },
    { value: 'cambodia', label: 'Cambodia', emoji: '🇰🇭' },
    { value: 'china', label: 'China', emoji: '🇨🇳' },
    { value: 'fiji', label: 'Fiji', emoji: '🇫🇯' },
    { value: 'hong_kong', label: 'Hong Kong', emoji: '🇭🇰' },
    { value: 'india', label: 'India', emoji: '🇮🇳' },
    { value: 'indonesia', label: 'Indonesia', emoji: '🇮🇩' },
    { value: 'japan', label: 'Japan', emoji: '🇯🇵' },
    { value: 'malaysia', label: 'Malaysia', emoji: '🇲🇾' },
    { value: 'maldives', label: 'Maldives', emoji: '🇲🇻' },
    { value: 'new_zealand', label: 'New Zealand', emoji: '🇳🇿' },
    { value: 'philippines', label: 'Philippines', emoji: '🇵🇭' },
    { value: 'singapore', label: 'Singapore', emoji: '🇸🇬' },
    { value: 'south_korea', label: 'South Korea', emoji: '🇰🇷' },
    { value: 'sri_lanka', label: 'Sri Lanka', emoji: '🇱🇰' },
    { value: 'tahiti', label: 'Tahiti', emoji: '🇵🇫' },
    { value: 'thailand', label: 'Thailand', emoji: '🇹🇭' },
    { value: 'vietnam', label: 'Vietnam', emoji: '🇻🇳' }
  ],
  'Americas': [
    { value: 'argentina', label: 'Argentina', emoji: '🇦🇷' },
    { value: 'brazil', label: 'Brazil', emoji: '🇧🇷' },
    { value: 'canada', label: 'Canada', emoji: '🇨🇦' },
    { value: 'chile', label: 'Chile', emoji: '🇨🇱' },
    { value: 'colombia', label: 'Colombia', emoji: '🇨🇴' },
    { value: 'costa_rica', label: 'Costa Rica', emoji: '🇨🇷' },
    { value: 'ecuador', label: 'Ecuador', emoji: '🇪🇨' },
    { value: 'hawaii', label: 'Hawaii', emoji: '🌺' },
    { value: 'mexico', label: 'Mexico', emoji: '🇲🇽' },
    { value: 'panama', label: 'Panama', emoji: '🇵🇦' },
    { value: 'peru', label: 'Peru', emoji: '🇵🇪' },
    { value: 'usa', label: 'USA', emoji: '🇺🇸' }
  ],
  'Caribbean': [
    { value: 'aruba', label: 'Aruba', emoji: '🇦🇼' },
    { value: 'bahamas', label: 'Bahamas', emoji: '🇧🇸' },
    { value: 'barbados', label: 'Barbados', emoji: '🇧🇧' },
    { value: 'bermuda', label: 'Bermuda', emoji: '🇧🇲' },
    { value: 'caribbean', label: 'Caribbean', emoji: '🏝️' },
    { value: 'cayman_islands', label: 'Cayman Islands', emoji: '🇰🇾' },
    { value: 'cuba', label: 'Cuba', emoji: '🇨🇺' },
    { value: 'curacao', label: 'Curaçao', emoji: '🇨🇼' },
    { value: 'dominican_republic', label: 'Dominican Republic', emoji: '🇩🇴' },
    { value: 'jamaica', label: 'Jamaica', emoji: '🇯🇲' },
    { value: 'puerto_rico', label: 'Puerto Rico', emoji: '🇵🇷' },
    { value: 'st_lucia', label: 'St. Lucia', emoji: '🇱🇨' },
    { value: 'turks_caicos', label: 'Turks & Caicos', emoji: '🇹🇨' },
    { value: 'us_virgin_islands', label: 'US Virgin Islands', emoji: '🇻🇮' }
  ],
  'Middle East & Africa': [
    { value: 'dubai', label: 'Dubai', emoji: '🇦🇪' },
    { value: 'egypt', label: 'Egypt', emoji: '🇪🇬' },
    { value: 'israel', label: 'Israel', emoji: '🇮🇱' },
    { value: 'jordan', label: 'Jordan', emoji: '🇯🇴' },
    { value: 'kenya', label: 'Kenya', emoji: '🇰🇪' },
    { value: 'morocco', label: 'Morocco', emoji: '🇲🇦' },
    { value: 'qatar', label: 'Qatar', emoji: '🇶🇦' },
    { value: 'saudi_arabia', label: 'Saudi Arabia', emoji: '🇸🇦' },
    { value: 'seychelles', label: 'Seychelles', emoji: '🇸🇨' },
    { value: 'south_africa', label: 'South Africa', emoji: '🇿🇦' },
    { value: 'tanzania', label: 'Tanzania', emoji: '🇹🇿' },
    { value: 'uae', label: 'UAE', emoji: '🇦🇪' }
  ]
};

// Flat array of all destinations (for backward compatibility and lookups)
const DESTINATIONS = Object.values(DESTINATIONS_BY_REGION).flat();

export default function B2BWidgetContent() {
  const { agency, getToken, refreshAgency } = useB2B();
  
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  // Specializations state
  const [tripTypes, setTripTypes] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [destinationSearch, setDestinationSearch] = useState('');
  
  // Offers state
  const [offers, setOffers] = useState([]);
  const [loadingOffers, setLoadingOffers] = useState(true);
  const [showOfferForm, setShowOfferForm] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [offerForm, setOfferForm] = useState({
    destination: '',
    description: '',
    price: '',
    originalPrice: '',
    imageUrl: '',
    badge: '',
    linkUrl: '',
    // New fields for contextual matching
    validMonths: [],      // e.g., ['march', 'april']
    duration: '',         // e.g., '5 days', '1 week'
    tripTypes: []         // e.g., ['beach', 'romantic']
  });

  // Month options for offer validity
  const MONTH_OPTIONS = [
    { value: 'january', label: 'Jan' },
    { value: 'february', label: 'Feb' },
    { value: 'march', label: 'Mar' },
    { value: 'april', label: 'Apr' },
    { value: 'may', label: 'May' },
    { value: 'june', label: 'Jun' },
    { value: 'july', label: 'Jul' },
    { value: 'august', label: 'Aug' },
    { value: 'september', label: 'Sep' },
    { value: 'october', label: 'Oct' },
    { value: 'november', label: 'Nov' },
    { value: 'december', label: 'Dec' }
  ];

  const DURATION_OPTIONS = [
    { value: 'weekend', label: 'Weekend (2-3 days)' },
    { value: '4-5 days', label: '4-5 days' },
    { value: '1 week', label: '~1 week' },
    { value: '10 days', label: '10 days' },
    { value: '2 weeks', label: '~2 weeks' }
  ];

  const OFFER_TRIP_TYPES = [
    { value: 'beach', label: 'Beach', emoji: '🏖️' },
    { value: 'romantic', label: 'Romantic', emoji: '💑' },
    { value: 'family', label: 'Family', emoji: '👨‍👩‍👧‍👦' },
    { value: 'adventure', label: 'Adventure', emoji: '🏔️' },
    { value: 'luxury', label: 'Luxury', emoji: '✨' },
    { value: 'budget', label: 'Budget', emoji: '💰' },
    { value: 'honeymoon', label: 'Honeymoon', emoji: '💒' },
    { value: 'cruise', label: 'Cruise', emoji: '🚢' }
  ];

  // Initialize from agency data - normalize to values
  useEffect(() => {
    if (agency) {
      // Normalize trip_types to values (handle string labels, objects, or values)
      const normalizedTripTypes = (agency.trip_types || []).map(t => {
        // If it's an object, extract value/name/label
        if (typeof t === 'object' && t !== null) {
          return (t.value || t.name || t.label || '').toLowerCase();
        }
        // If it's a string, check if it's a label and convert to value
        const str = String(t).toLowerCase();
        const found = TRIP_TYPES.find(x => x.label.toLowerCase() === str || x.value === str);
        return found ? found.value : str;
      }).filter(Boolean);
      
      // Normalize destinations to values (handle string labels, objects, or values)
      const normalizedDestinations = (agency.destinations || []).map(d => {
        // If it's an object, extract value/name/label
        if (typeof d === 'object' && d !== null) {
          return (d.value || d.name || d.label || '').toLowerCase();
        }
        // If it's a string, check if it's a label and convert to value
        const str = String(d).toLowerCase();
        const found = DESTINATIONS.find(x => x.label.toLowerCase() === str || x.value === str);
        return found ? found.value : str;
      }).filter(Boolean);
      
      console.log('Loaded trip types:', normalizedTripTypes);
      console.log('Loaded destinations:', normalizedDestinations);
      
      setTripTypes(normalizedTripTypes);
      setDestinations(normalizedDestinations);
    }
  }, [agency]);

  // Load offers on mount
  useEffect(() => {
    loadOffers();
  }, []);

  const loadOffers = async () => {
    const currentToken = getToken();
    if (!currentToken) {
      console.log('No token, skipping offers load');
      setLoadingOffers(false);
      return;
    }
    
    try {
      const res = await fetch('/api/b2b/agency/offers', {
        headers: { 'Authorization': `Bearer ${currentToken}` }
      });
      if (!res.ok) {
        console.error('Offers fetch failed:', res.status);
        return;
      }
      const data = await res.json();
      setOffers(data.offers || []);
    } catch (err) {
      console.error('Failed to load offers:', err);
    } finally {
      setLoadingOffers(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────
  // SPECIALIZATIONS HANDLERS
  // ─────────────────────────────────────────────────────────────────

  const toggleTripType = (value) => {
    setTripTypes(prev => {
      if (prev.includes(value)) {
        return prev.filter(t => t !== value);
      }
      if (prev.length >= 6) {
        alert('Maximum 6 trip types allowed');
        return prev;
      }
      return [...prev, value];
    });
  };

  const toggleDestination = (value) => {
    setDestinations(prev => {
      if (prev.includes(value)) {
        return prev.filter(d => d !== value);
      }
      if (prev.length >= 6) {
        alert('Maximum 6 destinations allowed');
        return prev;
      }
      return [...prev, value];
    });
  };

  const saveSpecializations = async () => {
    const currentToken = getToken();
    if (!currentToken) {
      alert('Not logged in. Please refresh the page.');
      return;
    }
    
    setSaving(true);
    setSaveSuccess(false);
    
    try {
      const res = await fetch('/api/b2b/agency/settings', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentToken}`
        },
        body: JSON.stringify({
          trip_types: tripTypes,
          destinations: destinations
        })
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Failed to save');
      }
      
      await refreshAgency();
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      alert('Failed to save: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────
  // OFFERS HANDLERS
  // ─────────────────────────────────────────────────────────────────

  const resetOfferForm = () => {
    setOfferForm({
      destination: '',
      description: '',
      price: '',
      originalPrice: '',
      imageUrl: '',
      badge: '',
      linkUrl: '',
      validMonths: [],
      duration: '',
      tripTypes: []
    });
    setEditingOffer(null);
    setShowOfferForm(false);
  };

  const handleOfferSubmit = async (e) => {
    e.preventDefault();
    
    if (!offerForm.destination || !offerForm.price) {
      alert('Destination and price are required');
      return;
    }

    const currentToken = getToken();
    if (!currentToken) {
      alert('Not logged in. Please refresh the page.');
      return;
    }

    setSaving(true);
    
    try {
      const method = editingOffer ? 'PATCH' : 'POST';
      const body = editingOffer 
        ? { id: editingOffer.id, ...offerForm }
        : offerForm;

      const res = await fetch('/api/b2b/agency/offers', {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentToken}`
        },
        body: JSON.stringify(body)
      });

      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.error || 'Failed to save offer');
      }

      await loadOffers();
      resetOfferForm();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const editOffer = (offer) => {
    setOfferForm({
      destination: offer.destination,
      description: offer.description || '',
      price: offer.price?.toString() || '',
      originalPrice: offer.original_price?.toString() || '',
      imageUrl: offer.image_url || '',
      badge: offer.badge || '',
      linkUrl: offer.link_url || '',
      validMonths: offer.valid_months || [],
      duration: offer.duration || '',
      tripTypes: offer.trip_types || []
    });
    setEditingOffer(offer);
    setShowOfferForm(true);
  };

  const deleteOffer = async (offerId) => {
    if (!confirm('Delete this offer?')) return;
    
    const currentToken = getToken();
    if (!currentToken) {
      alert('Not logged in. Please refresh the page.');
      return;
    }
    
    try {
      const res = await fetch(`/api/b2b/agency/offers?id=${offerId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${currentToken}` }
      });

      if (!res.ok) throw new Error('Failed to delete');
      
      await loadOffers();
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    }
  };

  // ─────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────

  return (
    <div>
      {/* Page Header */}
      <div className="b2b-page-header">
        <div>
          <h1 className="b2b-page-title">Widget Content</h1>
          <p className="b2b-page-subtitle">Customize what visitors see in your chat widget</p>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        
        {/* ═══════════════════════════════════════════════════════════
            SPECIALIZATIONS SECTION
        ═══════════════════════════════════════════════════════════ */}
        <div className="b2b-card">
          <div className="b2b-card-header">
            <h2 className="b2b-card-title">🎯 Your Specializations</h2>
            <p className="b2b-card-subtitle">
              Select up to 6 trip types and 6 destinations. These appear as badges in your chat widget.
            </p>
          </div>

          {/* Trip Types */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ 
              display: 'block', 
              fontSize: '13px', 
              fontWeight: 600, 
              color: '#374151',
              marginBottom: '12px' 
            }}>
              Trip Types ({tripTypes.length}/6)
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {TRIP_TYPES.map(type => (
                <button
                  key={type.value}
                  onClick={() => toggleTripType(type.value)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 14px',
                    borderRadius: '20px',
                    border: tripTypes.includes(type.value) 
                      ? '2px solid #10b981' 
                      : '1px solid #e5e7eb',
                    background: tripTypes.includes(type.value) 
                      ? '#d1fae5' 
                      : 'white',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: tripTypes.includes(type.value) ? 600 : 400,
                    color: '#374151',
                    transition: 'all 0.15s'
                  }}
                >
                  <span>{type.emoji}</span>
                  <span>{type.label}</span>
                  {tripTypes.includes(type.value) && <span>✓</span>}
                </button>
              ))}
            </div>
          </div>

          {/* Destinations - Option C: Pill Style + Search + Regions */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ 
              display: 'block', 
              fontSize: '13px', 
              fontWeight: 600, 
              color: '#374151',
              marginBottom: '12px' 
            }}>
              Destination Focus ({destinations.length}/6)
            </label>

            {/* Selected Tags at Top */}
            {destinations.length > 0 && (
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                padding: '12px',
                background: '#f9fafb',
                borderRadius: '8px',
                marginBottom: '12px'
              }}>
                {destinations.map(destValue => {
                  const dest = DESTINATIONS.find(d => d.value === destValue);
                  return dest ? (
                    <span
                      key={destValue}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '6px 10px',
                        background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                        borderRadius: '16px',
                        fontSize: '12px',
                        color: 'white',
                        fontWeight: 500
                      }}
                    >
                      <span>{dest.emoji}</span>
                      <span>{dest.label}</span>
                      <span 
                        onClick={() => toggleDestination(destValue)}
                        style={{ cursor: 'pointer', opacity: 0.8, marginLeft: '2px' }}
                      >×</span>
                    </span>
                  ) : null;
                })}
              </div>
            )}

            {/* Max Warning */}
            {destinations.length >= 6 && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 14px',
                background: '#fef3c7',
                border: '1px solid #f59e0b',
                borderRadius: '8px',
                fontSize: '13px',
                color: '#92400e',
                marginBottom: '12px'
              }}>
                ⚠️ Maximum 6 destinations selected. Remove one to add another.
              </div>
            )}

            {/* Search Box */}
            <div style={{ position: 'relative', marginBottom: '16px' }}>
              <span style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                fontSize: '14px'
              }}>🔍</span>
              <input
                type="text"
                placeholder="Search destinations... (e.g., Australia, Egypt, Peru)"
                value={destinationSearch}
                onChange={(e) => setDestinationSearch(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 16px 10px 40px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
              {destinationSearch && (
                <button
                  onClick={() => setDestinationSearch('')}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#9ca3af',
                    fontSize: '16px'
                  }}
                >×</button>
              )}
            </div>

            {/* Destinations by Region */}
            <div style={{ maxHeight: '350px', overflowY: 'auto', paddingRight: '8px' }}>
              {Object.entries(DESTINATIONS_BY_REGION).map(([region, regionDestinations]) => {
                // Filter destinations based on search
                const filteredDests = regionDestinations.filter(dest =>
                  !destinationSearch || 
                  dest.label.toLowerCase().includes(destinationSearch.toLowerCase())
                );
                
                // Skip region if no matches
                if (filteredDests.length === 0) return null;
                
                return (
                  <div key={region} style={{ marginBottom: '20px' }}>
                    <div style={{
                      fontSize: '12px',
                      fontWeight: 600,
                      color: '#9ca3af',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      marginBottom: '10px',
                      paddingBottom: '6px',
                      borderBottom: '1px solid #f3f4f6'
                    }}>
                      {region === 'Europe' && '🌍'} 
                      {region === 'Asia & Pacific' && '🌏'} 
                      {region === 'Americas' && '🌎'} 
                      {region === 'Caribbean' && '🏝️'} 
                      {region === 'Middle East & Africa' && '🌍'} {region}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {filteredDests.map(dest => {
                        const isSelected = destinations.includes(dest.value);
                        const isDisabled = !isSelected && destinations.length >= 6;
                        
                        return (
                          <button
                            key={dest.value}
                            onClick={() => !isDisabled && toggleDestination(dest.value)}
                            disabled={isDisabled}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '8px 14px',
                              borderRadius: '20px',
                              border: isSelected 
                                ? '2px solid #3b82f6' 
                                : '1px solid #e5e7eb',
                              background: isSelected 
                                ? '#dbeafe' 
                                : 'white',
                              cursor: isDisabled ? 'not-allowed' : 'pointer',
                              fontSize: '13px',
                              fontWeight: isSelected ? 600 : 400,
                              color: isDisabled ? '#9ca3af' : '#374151',
                              opacity: isDisabled ? 0.5 : 1,
                              transition: 'all 0.15s'
                            }}
                          >
                            <span>{dest.emoji}</span>
                            <span>{dest.label}</span>
                            {isSelected && <span>✓</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
              
              {/* No results message */}
              {destinationSearch && Object.values(DESTINATIONS_BY_REGION).every(region => 
                region.every(dest => !dest.label.toLowerCase().includes(destinationSearch.toLowerCase()))
              ) && (
                <div style={{
                  textAlign: 'center',
                  padding: '24px',
                  color: '#9ca3af',
                  fontSize: '14px'
                }}>
                  No destinations found matching "{destinationSearch}"
                </div>
              )}
            </div>
          </div>

          {/* Save Button */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button 
              className="b2b-btn b2b-btn-primary"
              onClick={saveSpecializations}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Specializations'}
            </button>
            {saveSuccess && (
              <span style={{ color: '#10b981', fontSize: '14px' }}>
                ✓ Saved!
              </span>
            )}
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════
            FEATURED OFFERS SECTION
        ═══════════════════════════════════════════════════════════ */}
        <div className="b2b-card">
          <div className="b2b-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
            <div>
              <h2 className="b2b-card-title">🔥 Featured Offers</h2>
              <p className="b2b-card-subtitle">
                Add up to 5 special deals. These appear as a carousel in your chat widget.
              </p>
            </div>
            {!showOfferForm && offers.length < 5 && (
              <button 
                className="b2b-btn b2b-btn-primary"
                onClick={() => setShowOfferForm(true)}
              >
                + Add Offer
              </button>
            )}
          </div>

          {/* Offer Form */}
          {showOfferForm && (
            <form 
              onSubmit={handleOfferSubmit}
              style={{
                background: '#f9fafb',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '20px'
              }}
            >
              <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '16px' }}>
                {editingOffer ? '✏️ Edit Offer' : '➕ New Offer'}
              </h3>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                {/* Destination */}
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}>
                    Destination *
                  </label>
                  <input
                    type="text"
                    className="b2b-form-input"
                    placeholder="e.g., Cancun, Mexico"
                    value={offerForm.destination}
                    onChange={(e) => setOfferForm(p => ({ ...p, destination: e.target.value }))}
                    required
                  />
                </div>

                {/* Description */}
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}>
                    Description
                  </label>
                  <input
                    type="text"
                    className="b2b-form-input"
                    placeholder="e.g., 5 nights • All-inclusive"
                    value={offerForm.description}
                    onChange={(e) => setOfferForm(p => ({ ...p, description: e.target.value }))}
                  />
                </div>

                {/* Price */}
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}>
                    Price *
                  </label>
                  <input
                    type="number"
                    className="b2b-form-input"
                    placeholder="1299"
                    value={offerForm.price}
                    onChange={(e) => setOfferForm(p => ({ ...p, price: e.target.value }))}
                    required
                  />
                </div>

                {/* Original Price */}
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}>
                    Original Price <span style={{ color: '#9ca3af' }}>(optional, for discounts)</span>
                  </label>
                  <input
                    type="number"
                    className="b2b-form-input"
                    placeholder="1899"
                    value={offerForm.originalPrice}
                    onChange={(e) => setOfferForm(p => ({ ...p, originalPrice: e.target.value }))}
                  />
                </div>

                {/* Image URL */}
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}>
                    Image URL
                  </label>
                  <input
                    type="url"
                    className="b2b-form-input"
                    placeholder="https://..."
                    value={offerForm.imageUrl}
                    onChange={(e) => setOfferForm(p => ({ ...p, imageUrl: e.target.value }))}
                  />
                </div>

                {/* Badge */}
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}>
                    Badge <span style={{ color: '#9ca3af' }}>(e.g., "30% OFF", "NEW")</span>
                  </label>
                  <input
                    type="text"
                    className="b2b-form-input"
                    placeholder="30% OFF"
                    value={offerForm.badge}
                    onChange={(e) => setOfferForm(p => ({ ...p, badge: e.target.value }))}
                    maxLength={15}
                  />
                </div>

                {/* Link URL */}
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}>
                    Link URL <span style={{ color: '#9ca3af' }}>(where clicking the offer goes)</span>
                  </label>
                  <input
                    type="url"
                    className="b2b-form-input"
                    placeholder="https://your-website.com/offer"
                    value={offerForm.linkUrl}
                    onChange={(e) => setOfferForm(p => ({ ...p, linkUrl: e.target.value }))}
                  />
                </div>
              </div>

              {/* ═══════════════════════════════════════════════════════
                  CONTEXTUAL MATCHING FIELDS
                  These help show the right offer during chat
              ═══════════════════════════════════════════════════════ */}
              <div style={{ 
                marginTop: '20px', 
                paddingTop: '20px', 
                borderTop: '1px solid #e5e7eb' 
              }}>
                <div style={{ 
                  fontSize: '13px', 
                  fontWeight: 600, 
                  color: '#374151', 
                  marginBottom: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  🎯 Smart Matching
                </div>
                <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '16px' }}>
                  Help us show this offer to the right customers during chat conversations.
                </p>

                {/* Valid Months */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '8px' }}>
                    Valid Months <span style={{ color: '#9ca3af' }}>(when is this offer available?)</span>
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {MONTH_OPTIONS.map(month => (
                      <button
                        key={month.value}
                        type="button"
                        onClick={() => {
                          setOfferForm(p => ({
                            ...p,
                            validMonths: p.validMonths.includes(month.value)
                              ? p.validMonths.filter(m => m !== month.value)
                              : [...p.validMonths, month.value]
                          }));
                        }}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '16px',
                          border: offerForm.validMonths.includes(month.value) 
                            ? '2px solid #10b981' 
                            : '1px solid #e5e7eb',
                          background: offerForm.validMonths.includes(month.value) 
                            ? '#d1fae5' 
                            : 'white',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: offerForm.validMonths.includes(month.value) ? 600 : 400
                        }}
                      >
                        {month.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Duration */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '8px' }}>
                    Trip Duration
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {DURATION_OPTIONS.map(dur => (
                      <button
                        key={dur.value}
                        type="button"
                        onClick={() => {
                          setOfferForm(p => ({
                            ...p,
                            duration: p.duration === dur.value ? '' : dur.value
                          }));
                        }}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '16px',
                          border: offerForm.duration === dur.value 
                            ? '2px solid #3b82f6' 
                            : '1px solid #e5e7eb',
                          background: offerForm.duration === dur.value 
                            ? '#dbeafe' 
                            : 'white',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: offerForm.duration === dur.value ? 600 : 400
                        }}
                      >
                        {dur.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Trip Types */}
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '8px' }}>
                    Trip Types <span style={{ color: '#9ca3af' }}>(what kind of travelers is this for?)</span>
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {OFFER_TRIP_TYPES.map(type => (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => {
                          setOfferForm(p => ({
                            ...p,
                            tripTypes: p.tripTypes.includes(type.value)
                              ? p.tripTypes.filter(t => t !== type.value)
                              : [...p.tripTypes, type.value]
                          }));
                        }}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '16px',
                          border: offerForm.tripTypes.includes(type.value) 
                            ? '2px solid #f59e0b' 
                            : '1px solid #e5e7eb',
                          background: offerForm.tripTypes.includes(type.value) 
                            ? '#fef3c7' 
                            : 'white',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: offerForm.tripTypes.includes(type.value) ? 600 : 400,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        <span>{type.emoji}</span>
                        <span>{type.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Form Actions */}
              <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                <button 
                  type="submit" 
                  className="b2b-btn b2b-btn-primary"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : (editingOffer ? 'Update Offer' : 'Add Offer')}
                </button>
                <button 
                  type="button" 
                  className="b2b-btn b2b-btn-secondary"
                  onClick={resetOfferForm}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {/* Offers List */}
          {loadingOffers ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
              Loading offers...
            </div>
          ) : offers.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px', 
              background: '#f9fafb',
              borderRadius: '12px',
              color: '#6b7280'
            }}>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>🎁</div>
              <div>No offers yet. Add your first special deal!</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {offers.map((offer, index) => (
                <div 
                  key={offer.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    padding: '12px 16px',
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px'
                  }}
                >
                  {/* Drag Handle (future: reordering) */}
                  <div style={{ color: '#d1d5db', cursor: 'grab' }}>⋮⋮</div>
                  
                  {/* Image Preview */}
                  <div style={{
                    width: '60px',
                    height: '45px',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    background: '#f3f4f6',
                    flexShrink: 0
                  }}>
                    {offer.image_url ? (
                      <img 
                        src={offer.image_url} 
                        alt={offer.destination}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <div style={{ 
                        width: '100%', 
                        height: '100%', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        fontSize: '20px'
                      }}>
                        🏝️
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: '14px', color: '#1f2937' }}>
                      {offer.destination}
                      {offer.badge && (
                        <span style={{
                          marginLeft: '8px',
                          padding: '2px 8px',
                          background: '#fef3c7',
                          color: '#92400e',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: 600
                        }}>
                          {offer.badge}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                      {offer.description}
                    </div>
                  </div>

                  {/* Price */}
                  <div style={{ textAlign: 'right' }}>
                    {offer.original_price && (
                      <div style={{ 
                        fontSize: '12px', 
                        color: '#9ca3af', 
                        textDecoration: 'line-through' 
                      }}>
                        ${parseFloat(offer.original_price).toLocaleString()}
                      </div>
                    )}
                    <div style={{ 
                      fontSize: '16px', 
                      fontWeight: 700, 
                      color: '#10b981' 
                    }}>
                      ${parseFloat(offer.price).toLocaleString()}
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => editOffer(offer)}
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '8px',
                        border: 'none',
                        background: '#dbeafe',
                        color: '#3b82f6',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                      title="Edit"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => deleteOffer(offer.id)}
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '8px',
                        border: 'none',
                        background: '#fee2e2',
                        color: '#ef4444',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Limit Notice */}
          {offers.length >= 5 && (
            <div style={{
              marginTop: '16px',
              padding: '12px',
              background: '#fef3c7',
              borderRadius: '8px',
              fontSize: '13px',
              color: '#92400e'
            }}>
              ⚠️ You've reached the maximum of 5 offers. Delete one to add more.
            </div>
          )}
        </div>

        {/* ═══════════════════════════════════════════════════════════
            PREVIEW SECTION
        ═══════════════════════════════════════════════════════════ */}
        <div className="b2b-card">
          <div className="b2b-card-header">
            <h2 className="b2b-card-title">👀 Preview</h2>
            <p className="b2b-card-subtitle">
              This is how your widget will look to visitors
            </p>
          </div>

          <div style={{ 
            background: '#f3f4f6', 
            padding: '24px', 
            borderRadius: '12px',
            display: 'flex',
            justifyContent: 'center'
          }}>
            {/* Mini Widget Preview */}
            <div style={{
              width: '320px',
              background: 'white',
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
            }}>
              {/* Header */}
              <div style={{
                background: agency?.brand_color || '#10b981',
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '18px'
                }}>
                  {agency?.logo_url ? (
                    <img src={agency.logo_url} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                  ) : (
                    agency?.logo_emoji || '✈️'
                  )}
                </div>
                <div>
                  <div style={{ color: 'white', fontWeight: 600, fontSize: '14px' }}>
                    {agency?.name || 'Your Agency'}
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '11px' }}>
                    ● Online
                  </div>
                </div>
              </div>

              {/* Body */}
              <div style={{ padding: '16px', background: '#f9fafb' }}>
                {/* Specializations */}
                {(tripTypes.length > 0 || destinations.length > 0) && (
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '10px', color: '#9ca3af', marginBottom: '6px', fontWeight: 600 }}>
                      WE SPECIALIZE IN
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {tripTypes.slice(0, 3).map(t => {
                        const type = TRIP_TYPES.find(x => x.value === t);
                        return (
                          <span key={t} style={{
                            padding: '3px 8px',
                            background: '#fef3c7',
                            borderRadius: '10px',
                            fontSize: '11px'
                          }}>
                            {type?.emoji} {type?.label}
                          </span>
                        );
                      })}
                      {destinations.slice(0, 3).map(d => {
                        const dest = DESTINATIONS.find(x => x.value === d);
                        return (
                          <span key={d} style={{
                            padding: '3px 8px',
                            background: '#dbeafe',
                            borderRadius: '10px',
                            fontSize: '11px'
                          }}>
                            {dest?.emoji} {dest?.label}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Offers Preview */}
                {offers.length > 0 && (
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '10px', color: '#9ca3af', marginBottom: '6px', fontWeight: 600 }}>
                      🔥 HOT DEALS
                    </div>
                    <div style={{ display: 'flex', gap: '8px', overflowX: 'auto' }}>
                      {offers.slice(0, 2).map(offer => (
                        <div key={offer.id} style={{
                          width: '120px',
                          flexShrink: 0,
                          background: 'white',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                        }}>
                          <div style={{
                            height: '50px',
                            background: offer.image_url 
                              ? `url(${offer.image_url}) center/cover` 
                              : 'linear-gradient(135deg, #cbd5e1, #94a3b8)'
                          }} />
                          <div style={{ padding: '6px 8px' }}>
                            <div style={{ fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {offer.destination}
                            </div>
                            <div style={{ fontSize: '12px', fontWeight: 700, color: '#10b981' }}>
                              ${parseFloat(offer.price).toLocaleString()}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quick Actions */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <div style={{
                    padding: '10px 12px',
                    background: 'white',
                    borderRadius: '8px',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                  }}>
                    <span>✈️</span>
                    <span style={{ fontWeight: 500 }}>Plan a new trip</span>
                  </div>
                  <div style={{
                    padding: '10px 12px',
                    background: 'white',
                    borderRadius: '8px',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                  }}>
                    <span>💬</span>
                    <span style={{ fontWeight: 500 }}>Talk to an advisor</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
